/**
 * Vietnam 업로드 파일의 종류를 **파일명에서** 판별한다.
 *
 * 서버 레코드에 종류 필드가 없다. `content_type`은 브라우저가 비워 주는 경우가 있고
 * (`.srt`는 대부분 빈 문자열이거나 `text/plain`), 과거에 올라간 레코드는 값이 제각각이라
 * 판정 근거로 삼을 수 없다. 확장자는 key에 그대로 박혀 있어 항상 읽을 수 있다.
 *
 * 자막을 인제스트하면 쓸모없는 Video 에셋과 실패하는 트랜스코딩 잡이 생기므로,
 * 이 판정이 목록에서 인제스트 대상을 가르는 기준이 된다.
 *
 * `stem`과 `lang`은 지금 화면에서 쓰지 않는다. 다음 단계인 **파일명 연동**
 * (`ep01.mp4` ↔ `ep01.vi.srt`)이 이 두 값을 키로 쓰게 되므로 미리 뽑아 둔다.
 */

/** 자막 확장자 → content_type. `.srt`는 표준 MIME이 없어 널리 쓰이는 값을 쓴다. */
export const SUBTITLE_CONTENT_TYPES: Record<string, string> = {
  srt: 'application/x-subrip',
  vtt: 'text/vtt',
  ass: 'text/x-ssa',
  ssa: 'text/x-ssa',
  smi: 'application/smil',
  sami: 'application/smil',
  sbv: 'text/plain',
  sub: 'text/plain',
};

export const SUBTITLE_EXTENSIONS = Object.keys(SUBTITLE_CONTENT_TYPES);

export type VietnamUploadKind = 'video' | 'subtitle';

export interface VietnamUploadFileInfo {
  kind: VietnamUploadKind;
  /** 소문자 확장자. 없으면 빈 문자열. */
  ext: string;
  /**
   * 언어 코드 — 자막 파일명의 마지막 두 번째 조각(`ep01.vi.srt` → `vi`)에서만 뽑는다.
   * 그런 조각이 없거나 언어처럼 생기지 않았으면 `undefined`.
   */
  lang?: string;
  /**
   * 확장자와 언어 조각을 뗀 이름(`ep01.vi.srt` → `ep01`, `ep01.mp4` → `ep01`).
   * 영상과 자막을 묶는 키가 될 값이다.
   */
  stem: string;
}

/** `Subtitle.LANG_CHOICES`(BE)와 같은 집합. 여기 없는 조각은 언어로 보지 않는다. */
const KNOWN_LANGS = ['en', 'ko', 'jp', 'es', 'zh', 'vi'];

export const isSubtitleFileName = (fileName: string): boolean => {
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
  return SUBTITLE_EXTENSIONS.includes(ext);
};

export const parseVietnamUploadFileName = (fileName: string): VietnamUploadFileInfo => {
  const segments = fileName.split('.');
  const ext = segments.length > 1 ? (segments.pop() as string).toLowerCase() : '';
  const kind: VietnamUploadKind = SUBTITLE_EXTENSIONS.includes(ext) ? 'subtitle' : 'video';

  // 언어 조각은 자막에서만 본다. `movie.2024.mp4`의 `2024`를 언어로 오인하지 않기 위해서다.
  const tail = segments[segments.length - 1]?.toLowerCase();
  const lang = kind === 'subtitle' && tail && KNOWN_LANGS.includes(tail) ? tail : undefined;
  if (lang) {
    segments.pop();
  }

  return { kind, ext, lang, stem: segments.join('.') || fileName };
};
